import { useState, useEffect } from "react";
import { Pencil, FolderPlus, Ban, Undo2, X, RefreshCw, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { LocationSidebarDetail } from "@/components/map/LocationSidebarDetail";
import { LocationSidebarEdit } from "@/components/map/LocationSidebarEdit";
import { AddToCollectionDialog } from "@/components/collections/AddToCollectionDialog";
import { isLocalDev } from "@/lib/is-local-dev";

interface LocationSidebarProps {
  locationId: string | null;
  onClose: () => void;
  onEnterSpotPlacement: (locationId: string) => void;
  onExitSpotPlacement: () => void;
  isSpotPlacementActive: boolean;
  onZoomToLocation: (locationId: string) => void;
  isPinEditActive?: boolean;
  hasPinMoved?: boolean;
  isSavingPin?: boolean;
  onEnterPinEdit?: () => void;
  onSavePinEdit?: () => void;
  onCancelPinEdit?: () => void;
}

function formatPrimaryType(type: string | null): string {
  if (!type) return "Unknown type";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function LocationSidebar({
  locationId,
  onClose,
  onEnterSpotPlacement,
  onExitSpotPlacement,
  isSpotPlacementActive,
  onZoomToLocation,
  isPinEditActive,
  hasPinMoved,
  isSavingPin,
  onEnterPinEdit,
  onSavePinEdit,
  onCancelPinEdit,
}: LocationSidebarProps) {
  const [view, setView] = useState<"detail" | "edit">("detail");
  const [showAddToCollection, setShowAddToCollection] = useState(false);

  const { data: location, isLoading } = trpc.locations.getById.useQuery(
    { id: locationId! },
    { enabled: !!locationId },
  );

  const utils = trpc.useUtils();

  const rejectMutation = trpc.locations.setRejected.useMutation({
    onSuccess: () => {
      utils.locations.getById.invalidate({ id: locationId! });
      utils.locations.getMapData.invalidate();
      utils.locations.reviewCount.invalidate();
    },
  });

  const screenshotMutation = trpc.locations.refetchScreenshot.useMutation({
    onSuccess: () => {
      utils.locations.getScreenshotUrl.invalidate({ locationId: locationId! });
      utils.locations.getById.invalidate({ id: locationId! });
    },
  });

  // Reset to detail view when locationId changes
  useEffect(() => {
    setView("detail");
  }, [locationId]);

  const handleClose = () => {
    if (isPinEditActive) {
      onCancelPinEdit?.();
    }
    if (isSpotPlacementActive) {
      onExitSpotPlacement();
    }
    onClose();
  };

  if (!locationId) return null;

  return (
    <>
      <div
        data-location-sidebar
        className="absolute top-0 right-0 bottom-0 z-20 w-[560px] 2xl:w-[560px] xl:w-[480px] lg:w-[440px] bg-background border-l shadow-xl flex flex-col"
        style={{
          transform: locationId ? "translateX(0)" : "translateX(100%)",
          transition: "transform 300ms ease-in-out",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b px-4 py-3 shrink-0">
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <>
                <Skeleton className="h-5 w-40 mb-1" />
                <Skeleton className="h-4 w-24" />
              </>
            ) : (
              <>
                <h2 className="text-sm font-semibold truncate">
                  {location?.name ?? "Location"}
                </h2>
                <p className="text-xs text-muted-foreground truncate">
                  {formatPrimaryType(location?.primary_type ?? null)}
                </p>
              </>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Edit toggle */}
            <Button
              size="icon"
              variant={view === "edit" ? "default" : "ghost"}
              className="size-8"
              title={view === "edit" ? "Viewing edit mode" : "Edit location"}
              onClick={() => setView(view === "edit" ? "detail" : "edit")}
            >
              <Pencil className="size-4" />
            </Button>

            {/* Pin edit toggle */}
            <Button
              size="icon"
              variant={isPinEditActive ? "default" : "ghost"}
              className="size-8"
              title={isPinEditActive ? "Cancel pin editing" : "Adjust pin location"}
              onClick={() => {
                if (isPinEditActive) {
                  onCancelPinEdit?.();
                } else {
                  onEnterPinEdit?.();
                }
              }}
            >
              <MapPin className="size-4" />
            </Button>

            {/* Refetch screenshot — local dev only */}
            {isLocalDev && (
              <Button
                size="icon"
                variant="ghost"
                className="size-8"
                title="Refetch screenshot"
                disabled={screenshotMutation.isPending || isLoading}
                onClick={() => {
                  if (locationId) screenshotMutation.mutate({ locationId });
                }}
              >
                <RefreshCw
                  className={`size-4 ${screenshotMutation.isPending ? "animate-spin" : ""}`}
                />
              </Button>
            )}

            {/* Add to collection */}
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              title="Add to collection"
              onClick={() => setShowAddToCollection(true)}
            >
              <FolderPlus className="size-4" />
            </Button>

            {/* Reject / Unreject */}
            <Button
              size="icon"
              variant={location?.is_rejected ? "ghost" : "ghost"}
              className="size-8"
              title={location?.is_rejected ? "Unreject location" : "Reject location"}
              disabled={rejectMutation.isPending || isLoading}
              onClick={() => {
                if (!location) return;
                rejectMutation.mutate({
                  locationId: location.id,
                  isRejected: !location.is_rejected,
                });
              }}
            >
              {location?.is_rejected ? (
                <Undo2 className="size-4" />
              ) : (
                <Ban className="size-4" />
              )}
            </Button>

            {/* Close */}
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              title="Close sidebar"
              onClick={handleClose}
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* Pin edit indicator bar */}
        {isPinEditActive && (
          <div className="flex items-center gap-2 border-b px-4 py-2 bg-amber-50 shrink-0">
            <span className="text-xs font-medium text-amber-800 flex-1">
              {hasPinMoved ? "Pin moved" : "Drag the pin to reposition"}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={onCancelPinEdit}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!hasPinMoved || isSavingPin}
              onClick={onSavePinEdit}
            >
              {isSavingPin ? "Saving..." : "Save"}
            </Button>
          </div>
        )}

        {/* Sliding content area */}
        <div className="flex-1 overflow-hidden relative">
          <div
            className="flex h-full"
            style={{
              width: "200%",
              transform:
                view === "detail" ? "translateX(0)" : "translateX(-50%)",
              transition: "transform 300ms ease-in-out",
            }}
          >
            {/* Detail view */}
            <div className="w-1/2 h-full">
              <LocationSidebarDetail
                locationId={locationId}
                onScreenshotClick={() => onZoomToLocation(locationId)}
                onEnterSpotPlacement={() =>
                  onEnterSpotPlacement(locationId)
                }
                onExitSpotPlacement={onExitSpotPlacement}
                isSpotPlacementActive={isSpotPlacementActive}
              />
            </div>

            {/* Edit view */}
            <div className="w-1/2 h-full overflow-y-auto">
              <LocationSidebarEdit
                locationId={locationId}
                onBack={() => setView("detail")}
                isPinEditActive={isPinEditActive}
                hasPinMoved={hasPinMoved}
                isSavingPin={isSavingPin}
                onEnterPinEdit={onEnterPinEdit}
                onSavePinEdit={onSavePinEdit}
                onCancelPinEdit={onCancelPinEdit}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add to collection dialog */}
      {locationId && (
        <AddToCollectionDialog
          open={showAddToCollection}
          onOpenChange={setShowAddToCollection}
          locationIds={[locationId]}
        />
      )}
    </>
  );
}
